import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { BreakpointObserver } from "@angular/cdk/layout";
import { BehaviorSubject, Subscription } from "rxjs";
import { take, filter, map } from "rxjs/operators";
import { AddressSummaryService } from "../../../../common/services/api/resources/addressSummary/address-summary.service";
import {
  Organization,
  OrganizationService
} from "../../../../common/services/api/resources/organization/organization.service";
import { AssignedUserAddress, Phone } from "../../../../common/services/api/resources/phone/phone";
import { PhoneService } from "../../../../common/services/api/resources/phone/phone.service";
import { views } from "../../../app/phone/views";
import { ModalMaterialComponent } from "../../shared/components/modal/modal-material.component";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { PhoneAdditionalDialogComponent } from "./phoneAdditionalDialog/phone-additional-dialog.component";
import { PackageService } from "../../../../common/services/api/resources/package/package.service";
import { AccountResourceService } from "../../../../common/services/api/resources/package/account-resource.service";
import { ApiPromiseSubject } from "@onsip/common/services/api/api-promise-state.service";
import { AdminTableAbstractComponent } from "../../abstractClass/admin-table-abstract.component";
import { FormatMacAddressPipe } from "../../shared/pipes/format-mac-address.pipe";
import { LineInfo } from "./phoneDetail/phone-detail.component";
import { createCsvFile } from "../../shared/helpers/csv-helper";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";

export interface PhoneItemInfo extends Phone {
  line1: string;
}

const csvHeader = ["MAC address", "Make & model", "Provisioned Status", "Phone Lines"];

@Component({
  selector: "onsip-phone-admin-page",
  templateUrl: "./phone-admin-page.component.html",
  styleUrls: ["./phone-admin-page.component.scss"],
  providers: [FormatMacAddressPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PhonesAdminComponent
  extends AdminTableAbstractComponent<PhoneItemInfo>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content") _dontUse = true;
  @HostBinding("class.onsip-admin-host-grid")
  hasPhones = new BehaviorSubject<boolean>(true);
  blf = new FormControl(false);
  userUnlimited = false;
  blfPricing!: String;
  selectedRows: Array<PhoneItemInfo["phoneId"]> = [];
  isAccountAdmin!: boolean;

  protected displayedColumnsRecord = {
    XSmall: ["macAddress", "actions"],
    Small: ["macAddress", "line", "actions"],
    Medium: ["macAddress", "line", "make", "actions"],
    Large: ["macAddress", "line", "make", "status", "actions"],
    XLarge: ["macAddress", "line", "make", "status", "actions"]
  };

  protected unsubscriber = new Subscription();

  constructor(
    private phoneService: PhoneService,
    private addressSummariesService: AddressSummaryService,
    private orgService: OrganizationService,
    private snackbar: SnackbarService,
    private router: Router,
    private packageService: PackageService,
    private resourceService: AccountResourceService,
    private macAddressPipe: FormatMacAddressPipe,
    private userService: UserService,
    protected dialog: MatDialog,
    protected translate: TranslateService,
    protected breakpointObserver: BreakpointObserver,
    protected cdr: ChangeDetectorRef
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit() {
    super.ngOnInit();
    this.setDataFilter();
    // initial browse fetch for the page
    this.unsubscriber.add(
      this.orgService.orgState
        .pipe(map(org => org[0].busyLampField))
        .subscribe(hasBLF => this.blf.setValue(hasBLF))
    );

    this.unsubscriber.add(
      this.packageService.getAssignedPrimaryPackages().subscribe(packages => {
        packages.map(pkg => {
          if (pkg.status === "user") {
            this.userUnlimited = true;
            this.cdr.markForCheck();
          }
        });
        this.cdr.markForCheck();
      })
    );
    this.unsubscriber.add(
      this.userService.selfUserIsAccountAdmin$.subscribe(isAccountAdmin => {
        this.isAccountAdmin = isAccountAdmin;
        if (isAccountAdmin) {
          this.getPricing();
        }
      })
    );
  }

  onExportCsv() {
    const parsedData = this.parseDataForCsv(this.dataSource.data);
    const fileName = "ONSIP phones";
    createCsvFile(parsedData, csvHeader, fileName);
  }

  ngOnDestroy(): void {
    super.ngOnInit();
    this.unsubscriber.unsubscribe();
  }

  trackByMacAddress(index: number, item: PhoneItemInfo): string {
    return item.macAddress;
  }

  phoneToPhoneData(phone: Phone): PhoneItemInfo {
    return {
      ...phone,
      line1:
        phone.assignedUserAddresses.find(address => address.line === "1")?.userAddress.name || ""
    };
  }

  addNewPhone(): void {
    const modal = this.dialog.open(PhoneAdditionalDialogComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"]
    });

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj) this.router.navigate([views.PHONE_SETUP]);
        })
    );
  }

  performDeleteRequest(phoneInfo: PhoneItemInfo): void {
    this.phoneService.phoneDelete(phoneInfo.phoneId).then(result => {
      if (result.status === "success") {
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", {
            value: "Phone"
          }),
          "success"
        );
        this.automaticallyResetBlf();
      } else {
        this.snackbar.openSnackBar(result.data.message, "error");
        this.phoneService.clearErrors();
      }
    });
  }

  getPricing(): void {
    this.resourceService.accountResourceBrowse();
    this.unsubscriber.add(
      this.resourceService.state
        .pipe(
          filter(state => !state.loading),
          map(state => {
            return Object.values(state.state);
          })
        )
        .pipe(
          map(resourcesArray => {
            return resourcesArray.find(res => res.name === "BusyLampField");
          })
        )
        .subscribe(res => {
          this.blfPricing = res?.monthlyRecurringCharge || "undetermined";
          this.cdr.markForCheck();
        })
    );
  }

  getPricingText(): string {
    if (this.userUnlimited) {
      return "Included in your current plan.";
    }
    return `$${this.blfPricing} per month`;
  }

  toggleBLF(event: MatSlideToggleChange): void {
    if (this.blf.value) {
      const confirmModal = this.dialog.open(ModalMaterialComponent, {
        panelClass: ["mat-typography", "onsip-dialog-universal-style"],
        data: {
          title: "Enable Busy Lamp Field",
          message:
            "Selecting Busy Lamp Field applies the feature on all phones on your PBX that support it. Your account will be charged $19.95/month for this feature. Are you sure you want to enable Busy Lamp Field on your PBX? ",
          primaryBtnText: "OK",
          primaryBtnFlat: true,
          secondaryBtnText: "Cancel",
          noSentenceCaseTitle: true
        }
      });

      this.unsubscriber.add(
        confirmModal
          .afterClosed()
          .pipe(take(1))
          .subscribe(res => {
            if (res && res.doPrimaryAction) {
              this.orgService
                .organizationEditBusyLampField(event.checked)
                .then(result => this.handleBLFSuccessError(result, event.checked));
            } else {
              this.blf.setValue(false);
            }
          })
      );
    } else {
      this.orgService
        .organizationEditBusyLampField(event.checked)
        .then(result => this.handleBLFSuccessError(result, event.checked));
    }
  }

  handleBLFSuccessError(result: ApiPromiseSubject<Organization>, checked: boolean): void {
    if (result.status === "success") {
      this.snackbar.openSnackBar(
        "Busy lamp field is " + (checked ? "enabled." : "disabled."),
        "success"
      );
    } else {
      this.snackbar.openSnackBar(result.data.message, "error");
      this.orgService.clearErrors();
    }
  }

  protected initDataSource(): void {
    this.addressSummariesService.addressSummaryBrowse();
    this.phoneService.phoneBrowse();

    this.unsubscriber.add(
      this.phoneService.orgState.subscribe(phones => {
        this.dataSource.data = phones.map(phone => this.phoneToPhoneData(phone));
        this.hasPhones.next(this.dataSource.data.length > 0);
        this.isDataObtained$.next(true);
      })
    );
  }

  protected getItemValueByColumn(item: PhoneItemInfo, columnName: string): string | number {
    switch (columnName) {
      case "macAddress":
        return item.macAddress.toLowerCase();
      case "line":
        return item?.line1.toLowerCase();
      case "make":
        return item.make.toLowerCase();
      case "status":
        return item.encryptionKey.toLowerCase();
      default:
        return item.make.toLowerCase();
    }
  }

  protected getItemName(): string {
    return "this phone";
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      // mac address
      const formatMac = this.macAddressPipe.transform(data.macAddress) || "";
      const mac = data.macAddress;
      // line 1
      const line1 = data.line1;
      // make and model
      const model = data.model;
      // provision status
      const provisionStatus = data.encryptionKey ? "Provisioned" : "Not Provisioned";

      return (
        formatMac.toLowerCase().includes(filterValue) ||
        mac.toLowerCase().includes(filterValue) ||
        line1.toLowerCase().includes(filterValue) ||
        model.toLowerCase().includes(filterValue) ||
        provisionStatus.toLowerCase().startsWith(filterValue)
      );
    };
  }

  protected automaticallyResetBlf() {
    this.unsubscriber.add(
      this.hasPhones
        .pipe(
          take(1),
          filter(hasPhones => !hasPhones)
        )
        .subscribe(() => {
          if (this.blf.value) {
            this.orgService.organizationEditBusyLampField(false);
          }
        })
    );
  }

  private parseDataForCsv(data: Array<PhoneItemInfo>) {
    const parsedData = data.map(row => {
      const rowData = [
        this.macAddressPipe.transform(row.macAddress) || "",
        row.model || "",
        row.encryptionKey ? "Provisioned" : "Not provisioned",
        `"${this.getLineDetailInfo(row.assignedUserAddresses)
          .map(item => `${item.line}:${item.name} (${item.type})\t`)
          .join(" ")}"`
      ];
      return rowData;
    });
    return parsedData as Array<Array<string>>;
  }

  private getLineDetailInfo(assignedUserAddresses: Array<AssignedUserAddress>) {
    const linesInfo: Array<LineInfo> = [];
    assignedUserAddresses.forEach(line => {
      const lineInfo = {
        name: line.userAddress.name
      };
      if (line.callsPerLine || line.busyLampField) {
        if (line.callsPerLine) {
          const callLine = {
            type: "Phone line",
            enabled: line.callsPerLine === "0" ? "enabled" : "disabled",
            line: line.line
          };
          const callLineInfo = { ...lineInfo, ...callLine } as LineInfo;
          linesInfo.push(callLineInfo);
        }
        if (line.busyLampField) {
          const blfLine = {
            type: "Busy lamp",
            enabled: "*disabled",
            line: line.busyLampField.line
          };
          const blfLineInfo = { ...lineInfo, ...blfLine } as LineInfo;
          linesInfo.push(blfLineInfo);
        }
        linesInfo.sort((a, b) => parseInt(a.line || "") - parseInt(b.line || ""));
      }
    });
    return linesInfo;
  }
}
